// DISPATCHARR_GUIDE_WHEEL_AXIS_R93
// Pure helpers for the guide's wheel handling, kept out of the component so
// the browser-specific cases can be unit-tested.
//
// The guide scrolls both ways in one element. Horizontal wheel movement is
// handled by the page (it keeps the hour header in step and stops the page
// behind the guide from panning), vertical movement is the browser's.
// Deciding "horizontal" by `deltaX !== 0` broke Firefox: unlike Chrome and
// Safari it does not lock a trackpad gesture to one axis, so nearly every
// vertical two-finger scroll carried a small deltaX, every such event was
// preventDefault'ed, its vertical component was lost, and the grid barely moved
// down while juddering sideways. The axis is now the dominant one, decided
// once per gesture and held until the wheel has been quiet for a moment.

export const WHEEL_GESTURE_GAP_MS = 250;
const LINE_PX = 16;

/**
 * Wheel deltas in pixels. Firefox reports a mouse wheel in lines
 * (deltaMode 1) and some inputs in pages (deltaMode 2).
 */
export function normalizeWheelDelta(event, viewport = {}) {
  let dx = Number(event.deltaX) || 0;
  let dy = Number(event.deltaY) || 0;
  if (event.deltaMode === 1) {
    dx *= LINE_PX;
    dy *= LINE_PX;
  } else if (event.deltaMode === 2) {
    dx *= viewport.clientWidth || 800;
    dy *= viewport.clientHeight || 600;
  }
  return { dx, dy };
}

/**
 * Decide which axis a wheel event belongs to and update the gesture state.
 *
 * @param {{ axis: 'x'|'y'|null, lastAt: number }} state mutable gesture state
 * @param {number} dx pixels
 * @param {number} dy pixels
 * @param {boolean} shiftKey shift + wheel always asks for horizontal
 * @param {number} nowMs
 * @returns {'x'|'y'} the axis this event scrolls
 */
export function wheelGestureAxis(state, dx, dy, shiftKey, nowMs) {
  if (state.axis && nowMs - state.lastAt > WHEEL_GESTURE_GAP_MS) {
    state.axis = null;
  }
  state.lastAt = nowMs;

  let axis;
  if (shiftKey) {
    axis = 'x';
  } else if (state.axis) {
    axis = state.axis;
  } else {
    axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
  }
  state.axis = axis;
  return axis;
}

/**
 * The horizontal amount an 'x' event scrolls: its own deltaX, or deltaY when
 * shift turned a vertical wheel into a horizontal request (Chrome keeps the
 * value in deltaY; Firefox already swaps it into deltaX).
 */
export function horizontalWheelAmount(dx, dy, shiftKey) {
  if (dx !== 0) return dx;
  return shiftKey ? dy : 0;
}
